import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type {
  LineItemData,
  OrderSummaryLineItem,
} from '../../types/OrderHistory.types';
import { getLineItemFulfillmentHistory } from '../../utils/fulfillmentHistoryUtils';
import type { ReorderableItem } from '../../types/ReorderableItems.types';
import type { OrderFulfillmentHistory } from '@vp/fulfillment-history';

interface ILineItemDataContext {
  item: OrderSummaryLineItem | ReorderableItem;
  orderNumber: string;
  fulfillmentHistory?: OrderFulfillmentHistory;
}

export const LineItemDataContext = createContext<ILineItemDataContext | null>(
  null
);

type LineItemDataProviderProps = {
  children: ReactNode;
  item: OrderSummaryLineItem | ReorderableItem;
  fulfillmentHistory?: OrderFulfillmentHistory;
  orderNumber: string;
};

/**
 * A context provider to make line item data available to all children components.
 *
 * LineItem data access should be requested via the `useLineItemData` for consistency and default null values.
 */
const LineItemDataProvider = ({
  children,
  item,
  orderNumber,
  fulfillmentHistory,
}: LineItemDataProviderProps) => {
  const lineItemDataContextValues = useMemo(
    () => ({
      item,
      orderNumber,
      fulfillmentHistory,
    }),
    [item, orderNumber, fulfillmentHistory]
  );

  return (
    <LineItemDataContext.Provider value={lineItemDataContextValues}>
      {children}
    </LineItemDataContext.Provider>
  );
};

/**
 *
 * @returns A standard line item data representation with `null` fallbacks for missing data.
 */
const useLineItemData = (): LineItemData => {
  const context = useContext(LineItemDataContext);

  const lineItemData = isReorderableItem(context?.item)
    ? getLineItemDataFromReorderableItem(context.item)
    : getLineItemDataFromOrderSummaryLineItem(context?.item);

  return {
    ...lineItemData,
    fulfillmentHistory:
      lineItemData.id && context?.fulfillmentHistory
        ? getLineItemFulfillmentHistory(
            context.fulfillmentHistory,
            lineItemData.id
          )
        : null,
    orderNumber: context?.orderNumber || '',
  };
};

const isReorderableItem = (
  item: ReorderableItem | OrderSummaryLineItem | undefined
): item is ReorderableItem => {
  return item && 'lineItemId' in item ? true : false;
};

const getLineItemDataFromOrderSummaryLineItem = (
  item?: OrderSummaryLineItem
): Omit<LineItemData, 'fulfillmentHistory' | 'orderNumber'> => {
  const trackingUrl = item?.trackingUrl || null;
  const isGroupItem = !!item?.groupInfo?.groupId;

  return {
    actions: {
      reorder: item?.isReorderable || false,
    },
    attributes: item?.displayProductAttributes || [],
    cmrd: item?.promisedArrivalDate || null,
    customOrderData: item?.customOrderData || null,
    deliverability: {
      itemDeliverable: item?.isDeliverable || false,
    },
    documentUrl: item?.documentUrl || null,
    editDocumentUrl: item?.editDocumentUrl || null,
    groupInfo: item?.groupInfo || null, // was || {}
    id: item?.id || null,
    isGroupItem,
    legacyProductConfigId: item?.legacyProductConfigId || null,
    livePreviewUrl: item?.livePreviewUrl || null,
    pricing: {
      totalDiscountedPrice: item?.totalDiscountedPrice || null,
      totalListPrice: item?.totalListPrice || null,
    },
    productConfigUrl: item?.productConfigUrl || null,
    productKey: item?.productKey || null,
    productName: item?.productName || null,
    productVersion: item?.productVersion || null,
    purcsData: item?.purcsData || null,
    quantity: item?.quantity || null,
    states: item?.states || [],
    trackingData: { trackingUrl },
  };
};

const getLineItemDataFromReorderableItem = (
  item?: ReorderableItem
): Omit<LineItemData, 'fulfillmentHistory' | 'orderNumber'> => {
  return {
    actions: {
      reorder: true,
    },
    attributes: item?.productData.productAttributes || [],
    cmrd: null,
    customOrderData: null,
    deliverability: {
      itemDeliverable: true,
    },
    documentUrl: item?.designData.docRefUrl || null,
    editDocumentUrl: item?.designData.editDocumentUrl || null,
    groupInfo: null,
    id: item?.lineItemId || null,
    isGroupItem: false,
    legacyProductConfigId: null,
    livePreviewUrl: item?.designData.livePreviewUrl || null,
    pricing: {
      totalDiscountedPrice: null,
      totalListPrice: null,
    },
    productConfigUrl: null,
    productKey: item?.productData.productKey || null,
    productName: item?.productData.productName || null,
    productVersion: item?.productData.productVersion || null,
    purcsData: item?.designData.purcsData || null,
    quantity: null,
    states: [],
    trackingData: { trackingUrl: null },
  };
};

export { LineItemDataProvider, useLineItemData };
